import { useState, useEffect } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { marcaAdapter } from "@/data";
import type { Marca } from "@/types/marca";

interface MarcaComboboxProps {
  value: string;
  onValueChange: (value: string) => void;
}

export function MarcaCombobox({ value, onValueChange }: MarcaComboboxProps) {
  const [open, setOpen] = useState(false);
  const [marcas, setMarcas] = useState<Marca[]>([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    loadMarcas();
  }, [search]);

  const loadMarcas = async () => {
    try {
      const response = await marcaAdapter.list({ search, filters: { ativo: true } });
      setMarcas(response.items);
    } catch (error) {
      console.error("Erro ao carregar marcas:", error);
    }
  };

  const selectedMarca = marcas.find((m) => m.id_marca === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between">
          {selectedMarca ? selectedMarca.nome : "Selecione uma marca..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar marca..." value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>Nenhuma marca encontrada.</CommandEmpty>
            <CommandGroup>
              {marcas.map((marca) => (
                <CommandItem
                  key={marca.id_marca}
                  value={marca.nome}
                  onSelect={() => {
                    onValueChange(marca.id_marca);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === marca.id_marca ? "opacity-100" : "opacity-0")} />
                  {marca.nome}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
